import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";

const initialState = {
  isLoading: false,
  error: null,
  reviews: [],
  averageRating: 0,
  totalReviews: 0,
  reviewComments: {}, // NEW: Holds comments per review_id
};

const accessToken =
  typeof window !== "undefined" ? localStorage.getItem("accessToken") : "";

const headers = {
  Authorization: `Bearer ${accessToken}`,
  "Content-Type": "application/json",
};

const reviewsSlice = createSlice({
  name: "reviews",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getAllReviewsSuccess(state, action) {
      state.isLoading = false;
      state.reviews = action.payload.reviews;
      state.averageRating = action.payload.average_rating;
      state.totalReviews = action.payload.total_reviews;
    },
    postReviewSuccess(state, action) {
      state.isLoading = false;
      state.reviews.unshift(action.payload);
    },
    likeReviewSuccess(state, action) {
      state.isLoading = false;
      const likedReview = state.reviews.find(
        (r) => r.id === action.payload.review_id
      );
      if (likedReview) {
        likedReview.likes = (likedReview.likes || 0) + 1;
      }
    },
    commentReviewSuccess(state, action) {
      state.isLoading = false;
      const review = state.reviews.find(
        (r) => r.id === action.payload.review_id
      );
      if (review) {
        review.comments = review.comments || [];
        review.comments.push(action.payload);
      }

      // Also update reviewComments in case it's used elsewhere
      if (state.reviewComments[action.payload.review_id]) {
        state.reviewComments[action.payload.review_id].push(action.payload);
      } else {
        state.reviewComments[action.payload.review_id] = [action.payload];
      }
    },
    getReviewCommentsSuccess(state, action) {
      state.isLoading = false;
      const { review_id, comments } = action.payload;
      state.reviewComments[review_id] = comments;
    },
  },
});

export const {
  startLoading,
  hasError,
  getAllReviewsSuccess,
  postReviewSuccess,
  likeReviewSuccess,
  commentReviewSuccess,
  getReviewCommentsSuccess,
} = reviewsSlice.actions;

export default reviewsSlice.reducer;

// ✅ Get Reviews (per product)
export const getReviews = (productId) => async (dispatch) => {
  try {
    dispatch(startLoading());
    const response = await axios.get(`/products/${productId}/reviews`);
    dispatch(getAllReviewsSuccess(response.data));
  } catch (error) {
    dispatch(
      hasError(error.response?.data?.message || error.message || "Failed to load reviews")
    );
  }
};

// ✅ Post Review
export const postReviews = (payload, toast, reset, setIsOpen) => async (dispatch) => {
  try {
    dispatch(startLoading());

    const response = await axios.post("/reviews", payload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    reset();
    setIsOpen(false);
    toast.success(response?.data?.message || "Review submitted successfully!");
    window.scrollTo({ top: 0, behavior: "smooth" });
  } catch (error) {
    const message =
      error.response?.data?.message || error.message || "Something went wrong";
    toast.error(message);
    dispatch(hasError(message));
  }
};

// ✅ Like a Review
export const likeReview = (payload, toast) => async (dispatch) => {
  try {
    const response = await axios.post(`/reviews/like`, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    toast.success(response.data.message || "Action successful");

    // You may re-fetch reviews if needed here:
    dispatch(getReviews(payload.product_id || ""));
  } catch (error) {
    toast.error(error.response?.data?.message || "Failed to like review");
  }
};

// ✅ Comment on a Review
export const commentOnReview = (payload, toast) => async (dispatch) => {
  try {
    dispatch(startLoading());
    const response = await axios.post(
      `/reviews/${payload.review_id}/comments`,
      payload,
      { headers }
    );
    dispatch(commentReviewSuccess(response.data.comment));
    toast.success("Comment added");
  } catch (error) {
    toast.error("Failed to post comment");
    dispatch(hasError(error.message));
  }
};

// ✅ Admin: Approve Review
export const approveReview = (id, toast) => async (dispatch) => {
  try {
    dispatch(startLoading());
    const response = await axios.put(`/admin/reviews/${id}/approve`, {}, { headers });
    toast.success(response.data.message || "Review approved");
  } catch (error) {
    toast.error("Failed to approve review");
    dispatch(hasError(error.message || "Approval failed"));
  }
};

// ✅ Admin: Reject Review
export const rejectReview = (id, toast) => async (dispatch) => {
  try {
    dispatch(startLoading());
    const response = await axios.put(`/admin/reviews/${id}/reject`, {}, { headers });
    toast.success(response.data.message || "Review rejected");
  } catch (error) {
    toast.error("Failed to reject review");
    dispatch(hasError(error.message || "Rejection failed"));
  }
};
